<template>
  <div class="styles-wrapper">
    <div v-for="(style, i) in state.styles">
      <div class="style-header">
        <span class="title">{{ $t(`skills.${style.id}.name`) }}</span>
        <div class="separator"></div>
      </div>
      <div class="styles">
        <div
          v-for="(wording, index) in style.style_types"
          class="style-tag"
          :class="{selected: (props.selectedStyles[style.id] as string[]).includes(wording.id)}"
          @click="clickOnStyle(i, index)"
          v-tooltip="$t(`skills.${style.id}.game.styles.${wording.id}.analysis.organization`)"
        >
          {{ $t(`skills.${style.id}.game.styles.${wording.id}.title`) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.styles-wrapper {
  flex-grow: 1;
  flex-basis: 0;
  
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-right: 10px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    padding-left: 4px;
    width: 3px;
    border-radius: 24px;
    background-color: var(--light-grey-color);
  }

  &::-webkit-scrollbar-thumb {
    background: $primary-color;
    padding: 4px;
    width: 3px;
    border-radius: 24px;
  }
}

.style-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  .title {
    flex-grow: 0;
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 14px;
    font-weight: 600;
  }

  .separator {
    flex-grow: 1;
    height: 0.5px;
    background: var(--secondary-color);
  }
}

.leadership-repartition-styles {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  .style-names {
    display: flex;
    flex-direction: column;
    gap: 9px;

    .name {
      display: flex;
      align-items: center;
      height: 27px;
    }
  }

  .style-info-group {
    display: flex;
    flex-direction: column;
    gap: 9px;

    .infos {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      height: 27px;

      .percent-bar-wrapper {
        width: 120px;
        height: 20px;
  
        .bar {
          background-color: var(--light-grey-color);
          border-radius: 4px;
          width: var(--style-percent);
          height: 20px;
  
          &.majority-style {
            background-color: $primary-color;
          }
        }
      }

      .percent-text {
        color: var(--secondary-color);
        text-align: center;
        font-family: $bioSans;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }

      .majority-style-tag {
        background-color: $primary-color;
        border-radius: 4px;
        padding: 6px;
        
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 11px;
        font-style: normal;
        font-weight: 600;
      }
    }
  }
}

.styles {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 5px 7px;
  gap: 6px;

  .style-tag {
    cursor: pointer;
    padding: 5px 7px;
    border-radius: 2px;
    user-select: none;

    color: var(--grey-50-color);
    font-family: $bioSans;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 100%; /* 14px */
    background-color: var(--light-grey-color);

    &.selected {
      color: $black-color;
      background-color: $primary-color;
    }
  }

  .leadership-repartition {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .style-names {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .style-infos {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .percent-bar-wrapper {
        width: 120px;
  
        .bar {
          background-color: var(--light-grey-color);
          border-radius: 4px;
          width: var(--style-percent);
  
          &.majority-style {
            background-color: $primary-color;
          }
        }
      }
  
      .majority-style-tag {
        background-color: $primary-color;
        border-radius: 4px;
        padding: 6px;
        
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
      }
    }

  }
} 
</style>

<script setup lang="ts">;
import { SkillInfo } from '@/stores/skills';
import { useCampaign } from '@/queries/campaigns.query';
import { ModuleLabel } from '@/types/campaign';

const route = useRoute();
const skillsStore = useSkillsStore();

const { data: campaign, isPending } = useCampaign();

const emits = defineEmits(["updateSelected"]);

const state = reactive({
  styles: [] as SkillInfo[],
});

const props = defineProps({
  selectedStyles: {},
});

function setModuleStyles() {
  if (isPending.value) return;
  state.styles = skillsStore.getCampaignStyles(campaign.value);
}

function clickOnStyle(styleIndex: number, wordingIndex: number | string) {
  const wording = state.styles[styleIndex].style_types[wordingIndex];

  let newSelectedStyles = Object.assign({}, props.selectedStyles);
  if (!(props.selectedStyles[state.styles[styleIndex].id] as string[]).includes(wording.id)) {
    newSelectedStyles[state.styles[styleIndex].id].push(state.styles[styleIndex].style_types[wordingIndex].id);
  } else {
    newSelectedStyles[state.styles[styleIndex].id] = (newSelectedStyles[state.styles[styleIndex].id] as string[]).filter((v) => v != wording.id);
  }
  emits('updateSelected', newSelectedStyles);
}

onUpdated(() => {
  if (state.styles.length === 0) {
    setModuleStyles();
  }
})

onMounted(() => {
  setModuleStyles();
});

</script>